import {
  EVIDENCE_MIME_TYPES,
  MAX_TZ_OFFSET_MIN,
  evidenceMediaTypeSchema,
  positionCodeSchema,
  uuidSchema,
} from '@opti-sea/core';
import { z } from 'zod';
import { EVIDENCE_PATH_PATTERN } from './evidence';
import { risqStatusSchema } from './status';

/**
 * Outbox mutations of the RISQ module (PLAN §3.2). The phone writes them in the same local
 * transaction as the change; `sync_push` (Task 8) validates them with the same schemas and applies
 * them through the same server functions as the dashboard.
 */

const isoDateSchema = z.iso.date();

export const setStatusPayloadSchema = z.object({
  event_id: uuidSchema,
  item_id: uuidSchema,
  from_status: risqStatusSchema,
  to_status: risqStatusSchema,
});

export const addNotePayloadSchema = z.object({
  event_id: uuidSchema,
  item_id: uuidSchema,
  text: z.string().trim().min(1).max(4000),
});

/** A tick on a base point (`wtc_item_id`) or on a company's own point (`company_wtc_item_id`). */
export const setWtcCheckPayloadSchema = z
  .object({
    event_id: uuidSchema,
    item_id: uuidSchema,
    wtc_item_id: uuidSchema.nullable(),
    company_wtc_item_id: uuidSchema.nullable(),
    checked: z.boolean(),
  })
  .refine((p) => (p.wtc_item_id === null) !== (p.company_wtc_item_id === null), {
    message: 'Exactly one of wtc_item_id and company_wtc_item_id',
  });

/** Vessel layer from the phone (Master); `vessel_id` null = company layer (dashboard only). */
export const setQuestionPositionsPayloadSchema = z.object({
  change_id: uuidSchema,
  company_id: uuidSchema,
  vessel_id: uuidSchema.nullable(),
  question_key: z.string().min(1).max(32),
  position_codes: z.array(positionCodeSchema).min(1).max(16),
});

export const revertQuestionPositionsPayloadSchema = z.object({
  change_id: uuidSchema,
  company_id: uuidSchema,
  vessel_id: uuidSchema.nullable(),
  question_key: z.string().min(1).max(32),
});

export const setInspectionDatePayloadSchema = z.object({
  vessel_id: uuidSchema,
  planned_on: isoDateSchema.nullable(),
});

/** Day the last voyage before the inspection started (Master); null clears it (Q26). */
export const setLastVoyagePayloadSchema = z.object({
  vessel_id: uuidSchema,
  started_on: isoDateSchema.nullable(),
});

/**
 * A Final checks mark (Task 6b) for the inspection planned on `inspection_on`. An item with an
 * input field is ticked when its value is given (`checked` = value not null; the server checks the
 * date format and the item's kind).
 */
export const setFinalCheckPayloadSchema = z
  .object({
    event_id: uuidSchema,
    vessel_id: uuidSchema,
    item_id: uuidSchema,
    inspection_on: isoDateSchema,
    checked: z.boolean(),
    value: z.string().trim().min(1).max(200).nullable(),
  })
  .refine((p) => p.value === null || p.checked, {
    message: 'A value is stored only with a check mark',
  });

/**
 * Evidence taken on the phone (Task 7): the metadata row of `risq_evidence` (PLAN §2.4). The
 * phone sends it only after both files are in Storage (§3.6 steps 5–6, Task 8); the server
 * accepts it only when both objects exist. One schema for photos and videos (D25).
 */
export const addEvidencePayloadSchema = z
  .object({
    evidence_id: uuidSchema,
    event_id: uuidSchema,
    item_id: uuidSchema,
    question_code: z.string().min(1).max(16),
    media_type: evidenceMediaTypeSchema,
    mime_type: z.string(),
    taken_at: z.iso.datetime({ offset: true }),
    tz_offset_min: z.number().int().min(-MAX_TZ_OFFSET_MIN).max(MAX_TZ_OFFSET_MIN),
    taken_position: positionCodeSchema,
    storage_path: z.string().regex(EVIDENCE_PATH_PATTERN),
    thumb_path: z.string().regex(EVIDENCE_PATH_PATTERN),
    bytes: z.number().int().positive(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    duration_ms: z.number().int().positive().nullable(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .refine((p) => p.mime_type === EVIDENCE_MIME_TYPES[p.media_type], {
    message: 'mime_type does not match media_type',
  })
  .refine((p) => (p.media_type === 'photo') === (p.duration_ms === null), {
    message: 'duration_ms is for videos only',
  })
  .refine((p) => p.storage_path.includes(p.evidence_id) && p.thumb_path.endsWith('_thumb.jpg'), {
    message: 'Paths must carry the evidence id; the thumbnail ends with _thumb.jpg',
  })
  .refine((p) => p.storage_path.includes(`_${p.taken_position}_`), {
    message: 'The position in the file name must be the position of the person who took it',
  });

/** Removal of evidence (soft delete; author, Master, DPA, Admin — PLAN §3.5). */
export const removeEvidencePayloadSchema = z.object({
  event_id: uuidSchema,
  evidence_id: uuidSchema,
  item_id: uuidSchema,
  media_type: evidenceMediaTypeSchema,
});

export const risqMutationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('risq.set_status'), payload: setStatusPayloadSchema }),
  z.object({ type: z.literal('risq.add_note'), payload: addNotePayloadSchema }),
  z.object({ type: z.literal('risq.set_wtc_check'), payload: setWtcCheckPayloadSchema }),
  z.object({
    type: z.literal('risq.set_question_positions'),
    payload: setQuestionPositionsPayloadSchema,
  }),
  z.object({
    type: z.literal('risq.revert_question_positions'),
    payload: revertQuestionPositionsPayloadSchema,
  }),
  z.object({
    type: z.literal('risq.set_inspection_date'),
    payload: setInspectionDatePayloadSchema,
  }),
  z.object({ type: z.literal('risq.set_last_voyage'), payload: setLastVoyagePayloadSchema }),
  z.object({ type: z.literal('risq.set_final_check'), payload: setFinalCheckPayloadSchema }),
  z.object({ type: z.literal('risq.add_evidence'), payload: addEvidencePayloadSchema }),
  z.object({ type: z.literal('risq.remove_evidence'), payload: removeEvidencePayloadSchema }),
]);
export type RisqMutation = z.infer<typeof risqMutationSchema>;
export type RisqMutationType = RisqMutation['type'];
export const RISQ_MUTATION_TYPES = risqMutationSchema.options.map(
  (o) => o.shape.type.value,
) as readonly RisqMutationType[];
